import type { BiomarkerExplanation } from './types';

/**
 * Registry of biomarker explanations and interactive tutorials.
 * Centralized so that it can be reused across tooltips, modal dialogs,
 * report generators, and educational panels.
 */
export const BIOMARKER_EXPLANATIONS: Record<string, BiomarkerExplanation> = {
  MYOSI: {
    id: 'MYOSI',
    name: 'Signal Intensity Myocardial (Relative Delta)',
    category: 'curve',
    shortDescription:
      'Dynamic myocardial signal intensity over the cardiac cycle, normalized to each territory\'s own End-Diastolic (ED) baseline so all curves start at 1.0.',
    clinicalRelevance:
      'Evaluates dynamic changes in tissue perfusion and blood volume across the cardiac cycle. Starting each territory at 1.0 isolates the relative percent change (% delta) in signal during systole and diastole, making regional contractility and perfusion changes directly comparable across vessels.',
    units: 'Normalized ratio (starts at 1.0 = 100%)',
    normalRange: 'Baseline 1.00; systolic peak / nadir varies with vascular bed',
    calculationSummary:
      'Myocardial pixel intensities are sampled per AHA segment across cine phases, split into LAD, RCA, and LCx territories, smoothed, and each curve is divided by its own Phase 0 value.',
    steps: [
      {
        id: 'step-territory-aggregation',
        title: 'Coronary Territory Splitting',
        shortLabel: '1. Territory Splitting',
        summary: 'Transition from a continuous short-axis myocardium into standard coronary vascular territories.',
        details: [
          'The short-axis myocardial ring is split into standard coronary territories: LAD, RCA, and LCx.',
          'Using the the insertion point, the territories are estimated from the AHA segments typically supplied by each artery.',
        ],
        animationAction: 'territories',
      },
      {
        id: 'step-segment-sampling',
        title: 'Signal Intensity Calculation',
        shortLabel: '2. Signal Calculation',
        summary: 'Extract median myocardial signal intensities for each segment across cine cardiac phases.',
        details: [
          'At each phase of the cine cycle, the median pixel intensity is calculated for each territory using the pixels within its corresponding myocardial segments.',
          'The mean signal intensity across segments is computed for each territory.',
          'Missing or artifact-corrupted phases are identified and interpolated to maintain continuity throughout the cine cycle.',
        ],
        animationAction: 'scatter',
      },
      {
        id: 'step-curve-generation',
        title: 'Cardiac Time Curves',
        shortLabel: '3. Time Curves',
        summary: 'Track myocardial signal intensity chronologically across cardiac phases from End-Diastole through End-Systole.',
        details: [
          'Multiple cardiac phases stack chronologically through the cardiac cycle.',
          'Each territory\'s signal evolves across phases, capturing dynamic myocardial perfusion and blood volume shifts.',
        ],
        animationAction: 'curves',
      },
      {
        id: 'step-relative-normalization',
        title: 'Relative Delta Normalization',
        shortLabel: '4. Self-Normalization',
        summary: 'Divide each territory curve by its own Phase 0 (ED) value so all curves start at 1.00.',
        details: [
          'Every territory curve is divided by its own initial value: SI_territory(t) / SI_territory(0).',
          'All curves are thus aligned to a baseline of 1.00 at Phase 0.',
          'This isolates the relative % change (delta) from the initial ED phase between LAD, RCA, and LCx.',
        ],
        animationAction: 'normalize-self',
      },
      {
        id: 'step-interpretation',
        title: 'Interpretation',
        shortLabel: '5. Interpretation',
        summary: 'Compare the relative changes between the 3 territories to identify disease.',
        details: [
          'The goal of this biomarker is to compare the relative changes between the 3 territories.',
          'In healthy myocardium, all territories exhibit synchronized perfusion dynamics throughout the cardiac cycle.',
          'For example, in coronary artery disease, the ischemic territory may show a blunted, depressed curve.',
        ],
        animationAction: 'interpretation',
      },
    ],
  },

  MYOSI_GlobalED: {
    id: 'MYOSI_GlobalED',
    name: 'Signal Intensity Myocardial (Norm to Global ED)',
    category: 'curve',
    shortDescription:
      'Dynamic myocardial signal intensity curves normalized to the Global End-Diastolic (ED) baseline, preserving inter-territorial baseline intensity differences.',
    clinicalRelevance:
      'Preserves baseline resting differences between coronary territories. If one territory is natively darker or brighter than the global myocardium at End-Diastole (e.g. due to resting ischemia, edema, or microvascular disease), this offset remains visible on the graph while still standardizing overall scale.',
    units: 'Normalized ratio (Global ED = 1.0)',
    normalRange: 'Baseline near 1.00; regional offsets reflect resting tissue differences',
    calculationSummary:
      'Myocardial pixel intensities are sampled per AHA segment across cine phases, split into LAD, RCA, and LCx, smoothed, and all curves are divided by the initial value of the Global curve at Phase 0.',
    steps: [
      {
        id: 'step-territory-aggregation',
        title: 'Coronary Territory Splitting',
        shortLabel: '1. Territory Splitting',
        summary: 'Transition from a continuous short-axis myocardium into standard coronary vascular territories.',
        details: [
          'The short-axis myocardial ring is split into standard coronary territories: LAD, RCA, and LCx.',
          'Using the the insertion point, the territories are estimated from the AHA segments typically supplied by each artery.',
        ],
        animationAction: 'territories',
      },
      {
        id: 'step-segment-sampling',
        title: 'Signal Intensity Calculation',
        shortLabel: '2. Signal Calculation',
        summary: 'Extract median myocardial signal intensities for each segment across cine cardiac phases.',
        details: [
          'At each phase of the cine cycle, the median pixel intensity is calculated for each territory using the pixels within its corresponding myocardial segments.',
          'The mean signal intensity across segments is computed for each territory.',
          'Missing or artifact-corrupted phases are identified and interpolated to maintain continuity throughout the cine cycle.',
        ],
        animationAction: 'scatter',
      },
      {
        id: 'step-curve-generation',
        title: 'Cardiac Time Curves',
        shortLabel: '3. Time Curves',
        summary: 'Track myocardial signal intensity chronologically across cardiac phases from End-Diastole through End-Systole.',
        details: [
          'Multiple cardiac phases stack chronologically through the cardiac cycle.',
          'Each territory\'s signal evolves across phases, capturing dynamic myocardial perfusion and blood volume shifts.',
        ],
        animationAction: 'curves',
      },
      {
        id: 'step-global-ed-normalization',
        title: 'Global ED Normalization',
        shortLabel: '4. Global ED Normalization',
        summary: 'Divide all territory curves by the Global ED value, preserving resting inter-territorial offsets.',
        details: [
          'Every territory curve is divided by Global(Phase 0): SI_territory(t) / SI_global(0).',
          'The Global curve starts at exactly 1.00. Regional resting offsets between territories remain preserved.',
        ],
        animationAction: 'normalize-global',
      },
      {
        id: 'step-interpretation',
        title: 'Interpretation',
        shortLabel: '5. Interpretation',
        summary: 'Identify resting hypoperfusion and blunted dynamic response across territories.',
        details: [
          'The goal of this biomarker is to display dynamic curves while preserving baseline resting signal differences across territories.',
          'In healthy myocardium, territories show comparable resting intensities and coordinated excursions.',
          'In coronary artery disease, the affected vascular bed displays a lower resting baseline and depressed dynamic excursion.',
        ],
        animationAction: 'interpretation',
      },
    ],
  },
};

/**
 * Check if an explanation exists for a given biomarker identifier.
 */
export function hasBiomarkerExplanation(biomarkerId: string | null | undefined): boolean {
  if (!biomarkerId) return false;
  return biomarkerId in BIOMARKER_EXPLANATIONS;
}

/**
 * Retrieve the biomarker explanation definition by identifier.
 */
export function getBiomarkerExplanation(
  biomarkerId: string | null | undefined,
): BiomarkerExplanation | null {
  if (!biomarkerId) return null;
  return BIOMARKER_EXPLANATIONS[biomarkerId] || null;
}

/**
 * Get all available biomarker explanation IDs.
 */
export function getAvailableExplanationIds(): string[] {
  return Object.keys(BIOMARKER_EXPLANATIONS);
}
